import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMode } from '../../context/ModeContext';
import { 
  FiGrid,
  FiUsers, 
  FiBox, 
  FiShield, 
  FiServer, 
  FiActivity, 
  FiSettings,
  FiFileText,
  FiBarChart,
  FiChevronLeft,
  FiChevronDown,
  FiChevronRight,
  FiHome,
  FiUser,
  FiLogOut
} from 'react-icons/fi';

const AdminSidebar = ({ open, collapsed, toggleSidebar, toggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { switchMode, availableModes, canSwitchModes, getModeConfig } = useMode();
  
  const [expandedSections, setExpandedSections] = useState({});
  
  const modeConfig = getModeConfig();

  // Get admin role for display - prioritize platformRole
  const adminRole = currentUser?.platformRole || currentUser?.orgRole;

  const getAdminLevel = () => {
    switch (adminRole) {
      case 'super_admin':
        return 'Super Admin';
      case 'platform_admin':
        return 'Platform Admin';
      case 'org_admin':
        // Check access level for org_admin
        if (currentUser?.orgAdminAccessLevel === 'limited') return 'Delegated Admin';
        if (currentUser?.orgAdminAccessLevel === 'read_only') return 'Security Reviewer';
        return 'Full Admin';
      default:
        return 'User';
    }
  };

  // Admin navigation items - using proper platform roles
  const adminNavItems = [
    {
      name: 'Dashboard',
      icon: FiGrid,
      path: '/admin/dashboard',
      roles: ['platform_admin', 'super_admin', 'org_admin']
    },
    {
      name: 'Users',
      icon: FiUsers,
      path: '/admin/users',
      roles: ['platform_admin', 'super_admin', 'org_admin']
    },
    {
      name: 'Organizations',
      icon: FiBox,
      path: '/admin/organizations',
      roles: ['platform_admin', 'super_admin']
    },
    {
      name: 'System',
      icon: FiServer,
      path: '/admin/system',
      roles: ['platform_admin', 'super_admin']
    },
    {
      name: 'Security',
      icon: FiShield,
      path: '/admin/security',
      roles: ['platform_admin', 'super_admin', 'org_admin']
    },
    {
      name: 'Logs',
      icon: FiFileText,
      path: '/admin/logs',
      roles: ['platform_admin', 'super_admin']
    },
    {
      name: 'Analytics',
      icon: FiBarChart,
      path: '/admin/analytics',
      roles: ['platform_admin', 'super_admin', 'org_admin']
    },
    {
      name: 'Settings',
      icon: FiSettings,
      path: '/admin/settings',
      roles: ['platform_admin', 'super_admin', 'org_admin']
    }
  ];

  // Filter nav items based on user's admin role
  const visibleNavItems = adminNavItems.filter(item => 
    item.roles.includes(adminRole)
  );

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    if (open && window.innerWidth < 768) {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Mode switcher options - using proper platform role names
  const modeOptions = [
    { value: 'individual', label: 'Personal', icon: FiUser },
    { value: 'organization', label: 'Organization', icon: FiBox },
    { value: 'admin', label: 'Platform Admin', icon: FiShield }
  ].filter(mode => availableModes.includes(mode.value));

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 
        ${collapsed ? 'w-16' : 'w-64'} 
        ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-red-900 text-white shadow-xl
        transition-all duration-300 ease-in-out
        flex flex-col
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-red-800">
          {!collapsed && (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-red-700 rounded-lg flex items-center justify-center">
                <FiShield className="w-5 h-5 text-red-200" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-red-100">Admin Panel</h1>
                <p className="text-xs text-red-300">{getAdminLevel()}</p>
              </div>
            </div>
          )}
          
          <button
            onClick={toggleCollapse}
            className="p-1 hover:bg-red-800 rounded transition-colors"
          >
            {collapsed ? (
              <FiChevronRight className="w-5 h-5 text-red-200" />
            ) : (
              <FiChevronLeft className="w-5 h-5 text-red-200" />
            )}
          </button>
        </div>

        {/* Mode Switcher */}
        {!collapsed && canSwitchModes && (
          <div className="p-4 border-b border-red-800">
            <div className="space-y-1">
              <button
                onClick={() => toggleSection('modes')}
                className="w-full flex items-center justify-between text-red-200 hover:text-white transition-colors"
              >
                <span className="text-sm font-medium">Switch Mode</span>
                <FiChevronDown className={`w-4 h-4 transition-transform ${
                  expandedSections.modes ? 'rotate-180' : ''
                }`} />
              </button>
              {expandedSections.modes && (
                <div className="mt-2 space-y-1">
                  {modeOptions.map((mode) => (
                    <button
                      key={mode.value}
                      onClick={() => switchMode(mode.value)}
                      className={`w-full flex items-center space-x-2 px-2 py-1 rounded text-sm transition-colors ${
                        modeConfig.mode === mode.value
                          ? 'bg-red-700 text-white'
                          : 'text-red-300 hover:text-white hover:bg-red-800'
                      }`}
                    >
                      <mode.icon className="w-4 h-4" />
                      <span>{mode.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-3">
            {visibleNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`
                  w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors duration-200
                  ${isActive(item.path)
                    ? 'bg-red-700 text-white shadow-sm'
                    : 'text-red-200 hover:bg-red-800 hover:text-white'
                  }
                  ${collapsed ? 'justify-center' : 'space-x-3'}
                `}
                title={collapsed ? item.name : ''}
              >
                <item.icon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
                {!collapsed && (
                  <span className="font-medium">{item.name}</span>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="border-t border-red-800 p-4">
          {!collapsed && (
            <div className="mb-3">
              <div className="text-xs text-red-300 mb-1">Logged in as</div>
              <div className="text-sm font-medium text-red-100 truncate">
                {currentUser?.name || currentUser?.email}
              </div>
            </div>
          )}
          
          <div className={`flex ${collapsed ? 'flex-col space-y-2' : 'space-x-2'}`}>
            <button
              onClick={() => switchMode('individual')}
              className="flex-1 flex items-center justify-center px-3 py-2 bg-red-800 hover:bg-red-700 rounded-lg transition-colors"
              title={collapsed ? 'Go to Personal Workspace' : ''}
            >
              <FiHome className="w-4 h-4" />
              {!collapsed && <span className="ml-2 text-sm">Personal</span>}
            </button>
            
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center px-3 py-2 bg-red-800 hover:bg-red-700 rounded-lg transition-colors"
              title={collapsed ? 'Logout' : ''}
            >
              <FiLogOut className="w-4 h-4" />
              {!collapsed && <span className="ml-2 text-sm">Logout</span>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;